import { createSlice } from '@reduxjs/toolkit';
import { dispatch, store } from '../store';
import { updateError } from './user';
import { callAdminApi, callApi } from '../../utils/apiCall';
import { resetFailureMessage } from '../../utils/passwordResetMessages';

// ---------------------------------------------------------------------------
// Scoped multi-practice user administration — FE state (869e5tyuf).
//
// Deliberately mirrors the SHAPE of `superAdminUsers.js`
// ({ isLoading, users:{results,count}, practices, filters, actionLoadingCount })
// so the ONE shared admin-user surface (src/pages/superAdmin/UserManagement.jsx,
// exported as `AdminUserManagement`) can render this slice with a scoped
// capability config instead of a forked page.
//
// Talks ONLY to the deployed `ScopedAdminUserViewSet`
// (apps/management/scoped_admin_views.py, PR #6252):
//   GET  /api/v1/management/managed-users/            (paginated: limit/offset)
//   GET  /api/v1/management/managed-users/{id}/
//   POST /api/v1/management/managed-users/{id}/attach_to_practice/
// The viewset inherits ExtendedLimitOffsetPagination (PAGE_SIZE=50) and has NO
// server-side filtering — so this slice paginates but sends no search filters.
//
// The BE is the sole authorization boundary (`IsScopedPracticeAdmin` +
// `Practice.managed_practice_ids`). This slice never re-derives who may be
// seen/attached; it renders exactly what the BE returns, including denials.
//
// Concurrency guards:
//   * `contextGeneration` + `activePracticeId` (F3): every practice-context
//     switch bumps the generation, clears `loadedPracticeId`, and abandons
//     any in-flight list request, so an A->B->A cache cannot commit a stale B
//     (or a late A) under the wrong header. Commit is verified against the
//     ACTIVE practice, not merely "a fetch is running".
//   * `latestAttachOpId` (F4): every attach captures an op id + the active
//     practice + target user; a late attach-A cannot dispatch success/failure
//     into a newer attach-B, nor fire its callback to close B's dialog or
//     refetch the wrong practice.
// ---------------------------------------------------------------------------

let listRequestSeq = 0;
let attachOpSeq = 0;

// Capability-probe dedupe/generation guards (nav gating). `inFlightProbe`
// caches the single live probe promise so N concurrent mounts issue exactly
// ONE request. `probeGeneration` is bumped by invalidateScopedAdmin() so a
// result from an older generation (an invalidation happened mid-flight) is
// dropped instead of clobbering the fresh determination.
let inFlightProbe = null;
let probeGeneration = 0;

const initialFilters = {
    page: 1,
    pageSize: 50, // matches BE ExtendedLimitOffsetPagination PAGE_SIZE
};

const initialState = {
    // capability probe — fail-closed: null = unknown, true/false decided by BE.
    isScopedAdmin: null,
    // whether a capability probe is currently in flight (hook re-dispatch guard).
    isProbing: false,

    // list surface (shape-compatible with superAdminUsers)
    isLoading: false,
    accessDenied: false,
    users: { results: [], count: 0 },
    practices: [],
    // Server-derived capability payload (task 869e6cckq) from the list
    // response's `managed_practice_capabilities` key: the caller's ACTIVE
    // managed practices, each with the exact roles they may assign there.
    // Fails CLOSED to [] (absent key, empty, error, or a stale/switched-away
    // context) — the attach picker never falls back to the header practice
    // list or the full role set.
    managedPracticeCapabilities: [],
    filters: { ...initialFilters },
    actionLoadingCount: 0,

    // practice-context / request bookkeeping
    activePracticeId: null,
    contextGeneration: 0,
    loadingPracticeId: null,
    loadingPage: null,
    loadingRequestId: null,
    loadedPracticeId: null,

    // attach op bookkeeping
    latestAttachOpId: null,

    // "Just disabled" recovery list (task 8) — ScopedAdminUserViewSet's
    // get_queryset filters to users with at least one ENABLED membership at
    // a managed practice, so a bulk Disable that disables EVERY managed
    // membership of a user makes them vanish from the next list fetch —
    // with no server-side way back into view for a scoped admin. Snapshots
    // of fully-disabled rows are kept HERE (client-side only, no BE change)
    // so the grid can keep showing them with an inline Re-enable action.
    // Scoped to the current session/practice-context: cleared on every
    // practice switch (setActivePractice, same as every other per-context
    // field above) and on logout (resetAll -> initialState).
    justDisabledRows: [],
};

export const slice = createSlice({
    name: 'scopedAdminUsers',
    initialState,
    reducers: {
        setIsScopedAdmin(state, action) {
            state.isScopedAdmin = action.payload;
        },
        setIsProbing(state, action) {
            state.isProbing = action.payload;
        },
        // Called on EVERY practice-context change (F3). Bumps generation,
        // clears the loaded marker (forces a fresh fetch on return), and
        // abandons any in-flight request so it can't commit under the new
        // practice.
        setActivePractice(state, action) {
            const next = action.payload;
            if (String(state.activePracticeId) === String(next)) return;
            state.activePracticeId = next;
            state.contextGeneration += 1;
            state.loadedPracticeId = null;
            state.loadingPracticeId = null;
            state.loadingPage = null;
            state.loadingRequestId = null;
            state.accessDenied = false;
            state.users = { results: [], count: 0 };
            // Fail closed on context switch: drop the previous practice's
            // capabilities until the new context's list response commits.
            state.managedPracticeCapabilities = [];
            // Normalize pagination to page 1 (offset 0) AS PART OF the context
            // transition, so the ONE fetch the shared shell issues for the new
            // practice starts at offset 0 — never inheriting the prior practice's
            // page/offset (P1). This is a single state update alongside the
            // context change, so the shell's [filters, contextValue] effect still
            // fires exactly once; no second fetch owner is introduced.
            state.filters.page = 1;
            // The recovery list (task 8) is scoped to the practice-context it
            // was built under — a "just disabled" row from practice A must not
            // bleed into practice B's grid after a switch.
            state.justDisabledRows = [];
        },
        // Full slice reset on logout / identity change (see resetScopedAdmin
        // thunk, which also bumps the module-level request/probe generations so
        // a late prior-session response is dropped). Returning initialState
        // clears rows, capabilities, activePracticeId, probe determination and
        // attach state so nothing bleeds into the next account.
        resetAll() {
            return initialState;
        },
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetFilters(state) {
            state.filters = { ...initialFilters };
        },
        startListLoad(state, action) {
            state.isLoading = true;
            state.accessDenied = false;
            state.loadingPracticeId = action.payload.practiceId;
            state.loadingPage = action.payload.page;
            state.loadingRequestId = action.payload.requestId;
        },
        listLoaded(state, action) {
            const { requestId, practiceId, data, capabilities } = action.payload;
            if (state.loadingRequestId !== requestId) return;
            if (String(state.activePracticeId) !== String(practiceId)) return;
            state.isLoading = false;
            state.accessDenied = false;
            state.users = data;
            // Committed under the SAME requestId/activePracticeId guard as the
            // rows, so a stale response can't set capabilities either.
            state.managedPracticeCapabilities = capabilities;
            state.loadedPracticeId = practiceId;
            state.loadingPracticeId = null;
            state.loadingPage = null;
            state.loadingRequestId = null;
        },
        listAccessDenied(state, action) {
            const { requestId, practiceId } = action.payload;
            if (state.loadingRequestId !== requestId) return;
            if (String(state.activePracticeId) !== String(practiceId)) return;
            state.isLoading = false;
            state.accessDenied = true;
            state.isScopedAdmin = false;
            state.users = { results: [], count: 0 };
            state.managedPracticeCapabilities = [];
            state.loadingPracticeId = null;
            state.loadingPage = null;
            state.loadingRequestId = null;
        },
        listLoadFailed(state, action) {
            const { requestId, practiceId } = action.payload;
            if (state.loadingRequestId !== requestId) return;
            if (String(state.activePracticeId) !== String(practiceId)) return;
            state.isLoading = false;
            state.loadingPracticeId = null;
            state.loadingPage = null;
            state.loadingRequestId = null;
        },
        setPractices(state, action) {
            state.practices = action.payload;
        },
        // Task 8 — recovery-list bookkeeping. addJustDisabledRows merges by
        // id (a re-disable replaces the older snapshot rather than
        // duplicating); removeJustDisabledRow drops one once it's been
        // successfully re-enabled.
        addJustDisabledRows(state, action) {
            const rows = action.payload || [];
            const byId = new Map(state.justDisabledRows.map((r) => [String(r.id), r]));
            rows.forEach((r) => byId.set(String(r.id), r));
            state.justDisabledRows = [...byId.values()];
        },
        removeJustDisabledRow(state, action) {
            const id = action.payload;
            state.justDisabledRows = state.justDisabledRows.filter((r) => String(r.id) !== String(id));
        },
        startAction(state, action) {
            state.actionLoadingCount += 1;
            if (action.payload?.attachOpId != null) {
                state.latestAttachOpId = action.payload.attachOpId;
            }
        },
        endAction(state) {
            state.actionLoadingCount = Math.max(0, state.actionLoadingCount - 1);
        },
    },
});

export default slice.reducer;
export const { setActivePractice, setFilters, resetFilters, setPractices } = slice.actions;

// ---------------------------------------------------------------------------
// Capability probe (nav gating). Mounted independently by SettingsMenu,
// SettingsPopover and MobileSettingsNav, so it must be race-safe:
//
//   * DEDUPE — `inFlightProbe` caches the single live probe promise. N
//     concurrent mounts observing `isScopedAdmin === null` all await the SAME
//     request; exactly ONE `GET managed-users/?limit=1` is issued.
//   * STICKY-TRUE — once authorized (200 -> true), the probe short-circuits
//     and never re-requests or downgrades. Only an explicit
//     invalidateScopedAdmin() (logout / re-auth) clears it, so a late
//     transient failure can NEVER flip an authorized caller back to hidden.
//   * STALE-DROP — each probe captures `probeGeneration`; if an invalidation
//     bumped the generation while the request was in flight, its result is
//     dropped (a newer determination owns the state).
//   * FAIL-CLOSED INITIAL — from null, only a real 200 sets true; 403 / any
//     non-200 / network error sets false.
// ---------------------------------------------------------------------------
export function probeScopedAdmin() {
    return () => {
        // Sticky-true: an authorized determination stands until explicit
        // invalidation — never re-probe, never risk a late downgrade.
        if (store.getState().scopedAdminUsers.isScopedAdmin === true) {
            return Promise.resolve();
        }
        // Dedupe: one shared live request for all concurrent mounts.
        if (inFlightProbe) return inFlightProbe;

        const gen = probeGeneration;
        dispatch(slice.actions.setIsProbing(true));
        const thisProbe = (async () => {
            let authorized = false;
            try {
                const response = await callAdminApi('/api/v1/management/managed-users/?limit=1');
                authorized = Boolean(response.ok && response.status === 200);
            } catch (error) {
                authorized = false; // fail-closed
            }
            // Drop a stale result: invalidateScopedAdmin()/resetScopedAdmin() ran
            // mid-flight, so a newer determination now owns the state.
            if (gen !== probeGeneration) return;
            dispatch(slice.actions.setIsProbing(false));
            if (authorized) {
                dispatch(slice.actions.setIsScopedAdmin(true));
            } else if (store.getState().scopedAdminUsers.isScopedAdmin !== true) {
                // Never downgrade an authorized true via a transient failure.
                dispatch(slice.actions.setIsScopedAdmin(false));
            }
        })();
        inFlightProbe = thisProbe;
        // Clear the dedupe handle ONLY if it still references THIS probe (P2): a
        // stale prior-session probe that already had its handle replaced (by a
        // reset + a newer session's probe) must NOT null the current handle, or
        // a later mount would see no in-flight probe and start a duplicate.
        thisProbe.finally(() => {
            if (inFlightProbe === thisProbe) inFlightProbe = null;
        });
        return thisProbe;
    };
}

// Explicit, deliberate invalidation — the ONLY thing that clears an authorized
// (true) determination. Bumps the generation so any in-flight probe's result
// is dropped, clears the dedupe cache, and resets to the fail-closed unknown
// state so the next mount re-probes. Wire to logout / re-auth as needed.
export function invalidateScopedAdmin() {
    return () => {
        probeGeneration += 1;
        inFlightProbe = null;
        dispatch(slice.actions.setIsProbing(false));
        dispatch(slice.actions.setIsScopedAdmin(null));
    };
}

// COMPLETE reset on logout / identity change — wired into JWTContext.logout and
// clearStore (mirrors the permissions invalidatePermissionsRequests + clear
// pattern). Bumps BOTH module generations (probe + list request) and clears the
// probe dedupe cache so any late prior-session probe OR list response is dropped
// (its requestId/generation no longer matches), then resets the whole slice to
// initial so no rows/capabilities/activePracticeId/determination bleed into the
// next account. MUST run before the next account renders.
export function resetScopedAdmin() {
    return () => {
        probeGeneration += 1;
        listRequestSeq += 1;
        inFlightProbe = null;
        dispatch(slice.actions.resetAll());
    };
}

// ---------------------------------------------------------------------------
// GET /api/v1/management/managed-users/  (paginated)
// `filters` = { page, pageSize }. Signature matches superAdminUsers.fetchUsers
// so the shared shell can dispatch it identically.
// ---------------------------------------------------------------------------
export function fetchManagedUsers(filters, { force = false } = {}) {
    return async () => {
        const state = store.getState().scopedAdminUsers;
        const practiceId = state.activePracticeId;
        const page = filters?.page || 1;
        const pageSize = filters?.pageSize || initialFilters.pageSize;

        // Loaded-guard only for the initial page of an already-loaded practice.
        if (!force && page === 1 && String(state.loadedPracticeId) === String(practiceId)
            && String(state.loadingPracticeId) !== String(practiceId)) {
            return;
        }

        // In-flight dedupe backstop (F3): an identical (practice, page) request
        // is already running — don't issue a duplicate. The shared shell is the
        // single fetch owner, but this guarantees no double request even if a
        // caller races it. A `force` refetch (post-mutation) always proceeds.
        if (!force && state.loadingRequestId != null
            && String(state.loadingPracticeId) === String(practiceId)
            && state.loadingPage === page) {
            return;
        }

        listRequestSeq += 1;
        const requestId = listRequestSeq;
        dispatch(slice.actions.startListLoad({ practiceId, page, requestId }));

        // Only surface a user-visible error if this response is STILL the active
        // request for the active practice at resolution time (F2). Captured
        // BEFORE dispatching listLoadFailed, which clears loadingRequestId.
        const isCurrent = () => {
            const s = store.getState().scopedAdminUsers;
            return s.loadingRequestId === requestId
                && String(s.activePracticeId) === String(practiceId);
        };

        const params = new URLSearchParams();
        params.append('limit', pageSize);
        params.append('offset', (page - 1) * pageSize);

        try {
            const response = await callAdminApi(`/api/v1/management/managed-users/?${params.toString()}`);
            if (response.ok && response.status === 200) {
                const data = response.data || {};
                // Fail closed: only a real array from the deployed BE key is
                // used; a missing/malformed key yields no picker options.
                const capabilities = Array.isArray(data.managed_practice_capabilities)
                    ? data.managed_practice_capabilities
                    : [];
                // Gate the nav determination on the SAME current-request check as
                // the row commit, so a late/post-reset 200 can't re-authorize a
                // logged-out or switched-away context (F1/F2).
                if (isCurrent()) {
                    dispatch(slice.actions.setIsScopedAdmin(true));
                }
                dispatch(slice.actions.listLoaded({
                    requestId,
                    practiceId,
                    data: { results: data.results || [], count: data.count ?? (data.results || []).length },
                    capabilities,
                }));
            } else if (response.status === 403) {
                dispatch(slice.actions.listAccessDenied({ requestId, practiceId }));
            } else {
                const current = isCurrent();
                dispatch(slice.actions.listLoadFailed({ requestId, practiceId }));
                if (current) {
                    dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to load managed users' }));
                }
            }
        } catch (error) {
            const current = isCurrent();
            dispatch(slice.actions.listLoadFailed({ requestId, practiceId }));
            if (current) {
                dispatch(updateError({ success: false, message: 'Failed to load managed users' }));
            }
        }
    };
}

// ---------------------------------------------------------------------------
// GET /api/v1/management/managed-users/{id}/  (single user detail; scoped
// memberships + practices_count). Kept for callers that need a fresh detail
// fetch; the list rows already carry practice_users + practices_count, so the
// expandable detail panel renders from the row without this call.
// ---------------------------------------------------------------------------
export function fetchManagedUserDetail(userId, onSuccess) {
    return async () => {
        try {
            const response = await callAdminApi(`/api/v1/management/managed-users/${userId}/`);
            if (response.ok && response.status === 200) {
                if (onSuccess) onSuccess(response.data);
            } else if (onSuccess) {
                onSuccess(null);
            }
        } catch (error) {
            if (onSuccess) onSuccess(null);
        }
    };
}

// ---------------------------------------------------------------------------
// POST /api/v1/management/managed-users/{id}/reset_password/
//
// Same three modes as the superadmin surface — the modes are shared code on
// the backend (PasswordResetModesMixin); only who may aim them at whom differs
// (assert_scoped_credential_reset_allowed). A 400 here is usually the guard
// talking, and its `detail` says which rule stopped it, so surface it verbatim
// rather than a generic failure.
function newIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : (r % 4) + 8;
        return v.toString(16);
    });
}

export function resetManagedUserPassword(userId, onSuccess, onComplete, onError) {
    return resetManagedUserPasswordWithMode(userId, 'email_link', onSuccess, onComplete, onError);
}

export function resetManagedUserPasswordWithMode(userId, mode, onSuccess, onComplete, onError) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const options = {};
            if (mode === 'temporary_credential') {
                options.headers = { 'Idempotency-Key': newIdempotencyKey() };
            }
            const response = await callAdminApi(
                `/api/v1/management/managed-users/${userId}/reset_password/`,
                'post',
                { mode },
                options,
            );
            if (response.ok) {
                if (mode === 'email_link') {
                    dispatch(updateError({
                        success: true,
                        message: response.data?.detail || 'Password reset email sent',
                    }));
                }
                if (onSuccess) onSuccess(response.data);
            } else {
                // Separates a backend refusal (whose `detail` names the rule)
                // from a request that never reached the server at all.
                const detail = resetFailureMessage(response);
                dispatch(updateError({ success: false, message: detail }));
                if (onError) onError(detail);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to reset password' }));
            if (onError) onError('Failed to reset password');
        }
        dispatch(slice.actions.endAction());
        if (onComplete) onComplete();
    };
}

// POST /api/v1/management/managed-users/{id}/attach_to_practice/
//
// Renders whatever the BE decides, honestly:
//   201/200 -> success (idempotent attach returns 200, created:false)
//   400     -> role-assignment rejection (privilege-escalation guard) —
//              the field message from `assert_role_assignment_allowed`
//   403     -> entry-gate denial
//   404     -> practice_id not one the caller manages (indistinguishable
//              from "doesn't exist" per BE contract)
//   409     -> archived membership conflict
//
// F4: every attach captures an op id + the active practice + target user.
// A late attach whose op id is no longer the latest, or whose practice is no
// longer active, does NOT commit state or fire its callback — so it cannot
// close a newer dialog or refetch the wrong practice.
// F6: success dispatches the global success snackbar via updateError().
// ---------------------------------------------------------------------------
export function attachUserToPractice(userId, { practiceId: targetPracticeId, role }, onSettled) {
    return async () => {
        attachOpSeq += 1;
        const opId = attachOpSeq;
        const capturedPractice = store.getState().scopedAdminUsers.activePracticeId;
        dispatch(slice.actions.startAction({ attachOpId: opId }));

        const opIsCurrent = () => {
            const s = store.getState().scopedAdminUsers;
            return s.latestAttachOpId === opId
                && String(s.activePracticeId) === String(capturedPractice);
        };

        try {
            const response = await callAdminApi(
                `/api/v1/management/managed-users/${userId}/attach_to_practice/`,
                'post',
                { practice_id: targetPracticeId, role },
            );
            dispatch(slice.actions.endAction());
            if (!opIsCurrent()) return false; // stale attach — drop silently

            if (response.ok && (response.status === 201 || response.status === 200)) {
                dispatch(updateError({ success: true, message: response.data?.detail || 'User attached to practice.' }));
                if (onSettled) onSettled({ success: true, data: response.data });
                return true;
            }

            const data = response.data || {};
            let message = data.detail;
            let field = '';
            if (!message && data.role) {
                field = 'role';
                message = Array.isArray(data.role) ? data.role[0] : data.role;
            }
            if (!message && response.status === 403) message = 'You do not have permission to perform this action.';
            if (!message && response.status === 404) message = 'That is not a practice you manage.';
            if (!message) message = 'Failed to attach user to practice.';
            dispatch(updateError({ success: false, message }));
            if (onSettled) onSettled({ success: false, status: response.status, message, field });
            return false;
        } catch (error) {
            dispatch(slice.actions.endAction());
            if (!opIsCurrent()) return false;
            dispatch(updateError({ success: false, message: 'Failed to attach user to practice.' }));
            if (onSettled) onSettled({ success: false, message: 'Failed to attach user to practice.' });
            return false;
        }
    };
}

// ---------------------------------------------------------------------------
// Bulk membership actions (task 5) — enable / disable / remove a selected
// managed user across every practice-membership the CALLER manages for them.
//
// Talks to the DEPLOYED `PracticeUserViewset` (apps/practice/views.py:325 —
// read-only inspection, no BE change made or requested):
//   PUT    /api/v1/practices/{practiceId}/users/{practiceUserId}/enable/
//   PUT    /api/v1/practices/{practiceId}/users/{practiceUserId}/disable/
//   DELETE /api/v1/practices/{practiceId}/users/{practiceUserId}/
// Its own get_queryset() scopes to `Practice.get_user_practices(user)` (any
// practice with an enabled PracticeUser row for the caller) — we never send
// an arbitrary practiceId; every (practiceId, practiceUserId) pair here is
// read from the ALREADY-fetched managed-users row, whose `practice_users[]`
// is itself pre-filtered server-side (ScopedAdminUserSerializer) to only the
// caller's managed practices, so a membership outside that set is never a
// candidate in the first place.
//
// No target-practice picker: unlike attach (which needs the caller to CHOOSE
// a practice to grant membership on), enable/disable/remove act on
// memberships that already exist and are already all "yours to manage" —
// so the action simply applies to every one of them, mirroring the
// no-picker simplicity of the superadmin Enable/Disable buttons.
// ---------------------------------------------------------------------------

// Reads the target's practice_users from whichever cache currently holds
// them: the live fetched list, OR (task 8) the "just disabled" recovery
// snapshot — a fully-disabled user is filtered out of the NEXT list fetch
// server-side (ScopedAdminUserViewSet.get_queryset requires is_enabled=True
// on at least one managed membership), so re-enabling them via the recovery
// row's inline action would otherwise find no pairs at all.
function managedPracticeUserPairsFor(userId) {
    const { users, justDisabledRows } = store.getState().scopedAdminUsers;
    const row = (users.results || []).find((u) => String(u.id) === String(userId))
        || (justDisabledRows || []).find((u) => String(u.id) === String(userId));
    return (row?.practice_users || [])
        .map((pu) => ({
            practiceId: pu.practice_id ?? pu.practice?.id ?? pu.practice,
            practiceUserId: pu.id,
        }))
        .filter((p) => p.practiceId != null && p.practiceUserId != null);
}

const BULK_MEMBERSHIP_VERBS = {
    enable: { path: 'enable/', method: 'put', past: 'Enabled' },
    disable: { path: 'disable/', method: 'put', past: 'Disabled' },
    remove: { path: '', method: 'delete', past: 'Removed' },
};

// Returns a Map<userId, {pairCount, failed}> so callers can tell WHICH
// selected users had every one of their managed memberships succeed (task
// 8 needs this: only a user with zero disable failures actually vanishes
// from the next list fetch, so only those get a recovery-list snapshot).
async function applyToManagedMemberships(userIds, verb) {
    const { path, method, past } = BULK_MEMBERSHIP_VERBS[verb];
    const outcomeByUser = new Map();
    let totalFailures = 0;
    await Promise.all(userIds.map(async (id) => {
        const pairs = managedPracticeUserPairsFor(id);
        let userFailures = 0;
        await Promise.all(pairs.map(async ({ practiceId, practiceUserId }) => {
            try {
                const url = `/api/v1/practices/${practiceId}/users/${practiceUserId}/${path}`;
                const response = method === 'delete' ? await callApi(url, method) : await callApi(url, method, {});
                if (!response.ok) userFailures += 1;
            } catch (error) {
                userFailures += 1;
            }
        }));
        totalFailures += userFailures;
        outcomeByUser.set(String(id), { pairCount: pairs.length, failed: userFailures });
    }));
    const totalPairs = [...outcomeByUser.values()].reduce((sum, o) => sum + o.pairCount, 0);
    if (totalPairs === 0) {
        dispatch(updateError({ success: false, message: 'No memberships you manage were found for the selected user(s).' }));
    } else if (totalFailures > 0) {
        dispatch(updateError({ success: false, message: `Failed to ${verb} ${totalFailures} of ${totalPairs} membership(s).` }));
    } else {
        dispatch(updateError({ success: true, message: `${past} ${totalPairs} membership(s).` }));
    }
    return outcomeByUser;
}

export function bulkEnableManaged(userIds, onComplete) {
    return async () => {
        const outcomeByUser = await applyToManagedMemberships(userIds, 'enable');
        // A user re-enabled successfully will reappear in the next real list
        // fetch — drop their recovery-list snapshot so the row doesn't
        // duplicate (server row + stale recovery row) once that fetch lands.
        outcomeByUser.forEach((outcome, id) => {
            if (outcome.pairCount > 0 && outcome.failed === 0) {
                dispatch(slice.actions.removeJustDisabledRow(id));
            }
        });
        onComplete?.();
    };
}

export function bulkDisableManaged(userIds, onComplete) {
    return async () => {
        // Snapshot BEFORE disabling — after this resolves and the caller
        // refetches, a user whose EVERY managed membership just got disabled
        // will be gone from the server response entirely (see
        // managedPracticeUserPairsFor's doc comment above), so this is the
        // only chance to keep them visible via the recovery list (task 8).
        //
        // Code review 4137, finding 2: tag each snapshot with the server
        // page/pageSize it was taken from. Without this, a recovery row
        // merges into EVERY page's grid unconditionally (mergedResults in
        // UserManagement.jsx just appends it to whatever page is currently
        // loaded) — disable someone on page 2, then browse to page 1, and
        // their stale row would incorrectly bleed onto page 1 too. The
        // shell only shows a recovery row on the exact
        // filters.page/pageSize it was captured under (or unconditionally
        // when client-filter mode has already pulled the full set into one
        // "page" — see showClientRows there).
        const { users, filters } = store.getState().scopedAdminUsers;
        const snapshotsById = new Map(
            (users.results || [])
                .filter((row) => userIds.some((id) => String(id) === String(row.id)))
                .map((row) => [String(row.id), {
                    ...row,
                    __disabledAtPage: filters.page,
                    __disabledAtPageSize: filters.pageSize,
                }])
        );
        const outcomeByUser = await applyToManagedMemberships(userIds, 'disable');
        const fullyDisabledRows = [];
        outcomeByUser.forEach((outcome, id) => {
            if (outcome.pairCount > 0 && outcome.failed === 0) {
                const snapshot = snapshotsById.get(id);
                if (snapshot) fullyDisabledRows.push(snapshot);
            }
        });
        if (fullyDisabledRows.length) {
            dispatch(slice.actions.addJustDisabledRows(fullyDisabledRows));
        }
        onComplete?.();
    };
}

// NOT wired to any UI entry point (see ScopedUserManagement.jsx —
// SCOPED_CONFIG.actions has no `bulkRemove`, and
// capabilities.bulkRemoveFromPractice is false). Security-reviewed: the
// backing DELETE endpoint has no guard against removing an ADMIN/SUPPORT/
// owner membership — a co-admin could irreversibly remove another admin or
// the practice owner. Parked (security-practiceuser-delete-no-guard.md)
// pending its own backend fix. Kept here, tested, so it can be re-wired
// with a single actions/capabilities change once that fix lands — this
// function alone has no reachable path from the UI.
export function bulkRemoveManaged(userIds, onComplete) {
    return async () => {
        await applyToManagedMemberships(userIds, 'remove');
        onComplete?.();
    };
}
